import { useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

import {
  DataTemplateResponse,
  DynamicValueResponse,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";
import { evaluateDependentCondition } from "@/domains/datablocks/fields/validators";
import { DataBlockFormFields } from "@/domains/datablocks/types";

/**
 * Hook to determine which fields should be hidden based on dependent validation rules
 * Returns a Set of field IDs that should be hidden
 */
export const useFieldVisibility = (
  dataTemplate: DataTemplateResponse,
  control: Control<DataBlockFormFields>,
): Set<number> => {
  // Get all dependent rules where alwaysVisible is NOT true
  const visibilityRules = useMemo(() => {
    return dataTemplate.validationRules.filter(
      (rule) =>
        rule.type === ValidationRuleTypes.DEPENDENT_REQUIRED &&
        rule.options?.alwaysVisible !== true,
    );
  }, [dataTemplate.validationRules]);

  // Watch the field values array
  const fieldValuesArray = useWatch({
    control,
    name: "fieldValues",
  });

  return useMemo(() => {
    const hidden = new Set<number>();

    if (!fieldValuesArray || visibilityRules.length === 0) {
      return hidden;
    }

    // Create a mapping of field ID to current value
    const fieldValuesDict = fieldValuesArray.reduce<
      Record<number, DynamicValueResponse>
    >((acc, fv) => {
      if (fv && typeof fv.field === "number") {
        acc[fv.field] = fv.value;
      }
      return acc;
    }, {});

    // For each visibility rule, check if conditions are met
    visibilityRules.forEach((rule) => {
      if (!rule.dependentOn) return;

      const triggerFieldIds = Object.keys(rule.dependentOn.value).map(Number);
      let anyConditionMet = false;

      // Check if any trigger condition is satisfied
      for (const triggerFieldId of triggerFieldIds) {
        const providedValue = fieldValuesDict[triggerFieldId];
        const expectedValue = rule.dependentOn.value[triggerFieldId];

        const conditionMet = evaluateDependentCondition(
          expectedValue,
          providedValue,
        );

        if (conditionMet) {
          anyConditionMet = true;
          break;
        }
      }

      // If no condition is met, hide the dependent fields
      if (!anyConditionMet) {
        rule.ruleFields?.forEach((fieldId) => hidden.add(fieldId));
      }
    });

    return hidden;
  }, [fieldValuesArray, visibilityRules]);
};
